import type { NativeStackNavigationOptions } from '@react-navigation/native-stack';

type StackAnimationType =
  | 'fade'
  | 'slide_from_right'
  | 'slide_from_left'
  | 'slide_from_bottom'
  | 'flip'
  | 'none';

interface AnimationConfig {
  animation: StackAnimationType;
  animationDuration: number;
}

// Animation presets for different screen transitions
export const SCREEN_ANIMATIONS: Record<string, AnimationConfig> = {
  // Smooth fade transition - good for splash screens
  fade: {
    animation: 'fade',
    animationDuration: 300,
  },

  // Standard slide from right - iOS default style
  slideRight: {
    animation: 'slide_from_right',
    animationDuration: 400,
  },

  // Fast slide from right - for quick transitions
  slideRightFast: {
    animation: 'slide_from_right',
    animationDuration: 300,
  },

  // Slide from bottom - modal-like appearance
  slideBottom: {
    animation: 'slide_from_bottom',
    animationDuration: 350,
  },

  // Slide from left - back navigation feel
  slideLeft: {
    animation: 'slide_from_left',
    animationDuration: 350,
  },

  // Flip animation - dramatic transition
  flip: {
    animation: 'flip',
    animationDuration: 400,
  },

  // No animation - instant transition
  none: {
    animation: 'none',
    animationDuration: 0,
  },
};

// Screen-specific animation configurations
export const getScreenOptions = (
  animationType: keyof typeof SCREEN_ANIMATIONS,
  options: Partial<NativeStackNavigationOptions> = {},
): NativeStackNavigationOptions => ({
  headerShown: false,
  gestureEnabled: true,
  gestureDirection: 'horizontal',
  ...SCREEN_ANIMATIONS[animationType],
  ...options,
});

// Predefined screen configurations
export const SCREEN_CONFIGS = {
  splash: () =>
    getScreenOptions('fade', {
      gestureEnabled: false,
    }),

  auth: () =>
    getScreenOptions('slideRight', {
      animationTypeForReplace: 'push',
    }),

  register: () =>
    getScreenOptions('slideRight', {
      animationDuration: 350,
    }),

  registerStep: () => getScreenOptions('slideRightFast'),

  main: () =>
    getScreenOptions('slideBottom', {
      animationTypeForReplace: 'push',
      gestureEnabled: false,
    }),

  modal: () =>
    getScreenOptions('slideBottom', {
      presentation: 'modal',
      gestureDirection: 'vertical',
    }),

  detail: () =>
    getScreenOptions('slideRight', {
      animationDuration: 300,
      animationTypeForReplace: 'push',
    }),
};
